import { useState, useRef, useEffect, useCallback } from "react";
import { api, mediaUrl } from "@/lib/api";
import ChatRecipeCard from "@/components/ChatRecipeCard";
import { toast } from "sonner";
import { Plus, ArrowUp, Sparkles, X, Loader2, Mic, Trash2, MessageSquare, PenSquare } from "lucide-react";

const newId = () => (crypto?.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(16).slice(2)}`);

function TypingBubble() {
  return (
    <div className="flex items-center gap-1.5 px-4 py-3 rounded-2xl rounded-tl-md bg-black/5 w-fit">
      {[0, 1, 2].map((i) => (
        <span key={i} className="w-2 h-2 rounded-full bg-nerd-light/40 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
      ))}
    </div>
  );
}

export default function Create() {
  const [messages, setMessages] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(newId());
  const [input, setInput] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [listening, setListening] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const fileInput = useRef(null);
  const scrollRef = useRef(null);
  const stickBottomRef = useRef(false);
  const taRef = useRef(null);
  const recognitionRef = useRef(null);
  const voiceBaseRef = useRef("");

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await api.get(`/chat/sessions`);
      return data.sessions || [];
    } catch { return []; }
  }, []);

  useEffect(() => {
    return () => { try { recognitionRef.current?.stop(); } catch { /* */ } };
  }, []);

  useEffect(() => {
    (async () => {
      const list = await loadSessions();
      setSessions(list);
      setLoadingHistory(false);
    })();
  }, [loadSessions]);

  useEffect(() => {
    if (!scrollRef.current) return;
    if (stickBottomRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [messages, sending]);

  const autoGrow = () => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 160) + "px";
  };

  const onFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please attach an image"); return; }
    setFile(f); setPreview(URL.createObjectURL(f));
  };

  const clearAttachment = () => { setFile(null); setPreview(""); if (fileInput.current) fileInput.current.value = ""; };

  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice dictation isn't supported in this browser"); return; }
    if (listening) { try { recognitionRef.current?.stop(); } catch { /* */ } return; }
    const rec = new SR();
    rec.lang = "en-US";
    rec.interimResults = true;
    rec.continuous = true;
    voiceBaseRef.current = input ? input.trim() + " " : "";
    rec.onresult = (e) => {
      let transcript = "";
      for (let i = 0; i < e.results.length; i++) transcript += e.results[i][0].transcript;
      setInput(voiceBaseRef.current + transcript);
      autoGrow();
    };
    rec.onend = () => setListening(false);
    rec.onerror = (e) => { setListening(false); if (e.error === "not-allowed") toast.error("Microphone access denied"); };
    recognitionRef.current = rec;
    try { rec.start(); setListening(true); } catch { /* */ }
  };

  const startNewChat = () => {
    setSessionId(newId());
    setMessages([]);
    setInput("");
    clearAttachment();
    setSidebarOpen(false);
  };

  const openChat = async (sid) => {
    if (sid === sessionId) { setSidebarOpen(false); return; }
    setSessionId(sid);
    setSidebarOpen(false);
    setLoadingHistory(true);
    try {
      const { data } = await api.get(`/chat/history`, { params: { session_id: sid } });
      stickBottomRef.current = false;
      setMessages(data.messages || []);
      requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: 0 }));
    } catch { setMessages([]); } finally { setLoadingHistory(false); }
  };

  const deleteChat = async (sid, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/chat/sessions/${sid}`);
      setSessions((s) => s.filter((x) => x.session_id !== sid));
      if (sid === sessionId) startNewChat();
      toast.success("Chat deleted");
    } catch { toast.error("Couldn't delete chat"); }
  };

  const send = async () => {
    const text = input.trim();
    if ((!text && !file) || sending) return;

    const optimistic = { id: `tmp-${Date.now()}`, role: "user", text, image_url: preview || "", recipe: null, _local: true };
    stickBottomRef.current = true;
    setMessages((m) => [...m, optimistic]);
    setInput(""); if (taRef.current) taRef.current.style.height = "auto";
    const attached = file;
    clearAttachment();
    setSending(true);

    try {
      const fd = new FormData();
      fd.append("message", text);
      fd.append("session_id", sessionId);
      if (attached) fd.append("file", attached);
      const { data } = await api.post("/chat/message", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMessages((m) => {
        const withoutTmp = m.filter((x) => x.id !== optimistic.id);
        return [...withoutTmp, data.user_message, data.assistant_message];
      });
      setSessions(await loadSessions());
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
      toast.error("Couldn't get a response. Try again.");
    } finally { setSending(false); }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const empty = !loadingHistory && messages.length === 0;

  const SidebarBody = (
    <>
      <div className="p-3">
        <button data-testid="new-chat-btn" onClick={startNewChat}
          className="w-full inline-flex items-center gap-2 justify-center bg-nerd-light text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:opacity-90 transition-opacity">
          <PenSquare className="w-4 h-4" /> New chat
        </button>
      </div>
      <div className="px-3 pb-1">
        <p className="text-[11px] font-mono-x uppercase tracking-widest text-nerd-light/40 px-1">Recent chats</p>
      </div>
      <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-0.5" data-testid="chat-sessions-list">
        {sessions.length === 0 && (
          <p className="text-xs text-nerd-light/40 px-3 py-4">No saved chats yet. Start cooking and they'll appear here.</p>
        )}
        {sessions.map((s) => (
          <div key={s.session_id} data-testid={`chat-session-${s.session_id}`} onClick={() => openChat(s.session_id)}
            className={`group flex items-center gap-2 px-3 py-2.5 rounded-xl cursor-pointer transition-colors ${
              s.session_id === sessionId ? "bg-black/8" : "hover:bg-black/5"}`}>
            <MessageSquare className="w-4 h-4 shrink-0 text-nerd-light/40" />
            <span className="flex-1 truncate text-sm text-nerd-light/85">{s.title}</span>
            <button data-testid={`delete-session-${s.session_id}`} onClick={(e) => deleteChat(s.session_id, e)}
              className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-nerd-light/40 hover:text-red-500 hover:bg-red-500/10 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-all"
              title="Delete chat">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div className="fixed inset-0 top-16 flex bg-white">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-72 border-r border-black/8 bg-nerd-card/40">
        {SidebarBody}
      </aside>

      {/* Mobile drawer */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 top-16 z-40 flex" data-testid="mobile-chat-drawer">
          <div className="w-72 max-w-[80%] bg-white border-r border-black/8 flex flex-col shadow-2xl animate-fade-up">
            {SidebarBody}
          </div>
          <div className="flex-1 bg-black/30" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="shrink-0 border-b border-black/8 px-4 sm:px-6">
          <div className="max-w-3xl mx-auto flex items-center h-14">
            <button data-testid="sidebar-toggle-btn" onClick={() => setSidebarOpen(true)}
              className="lg:hidden inline-flex items-center -ml-1 px-3 py-1.5 rounded-lg text-sm font-semibold text-nerd-light hover:bg-black/5 transition-colors" title="Recent chats">
              Recents
            </button>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} data-testid="chat-scroll" className="flex-1 overflow-y-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto py-6 space-y-5">
            {empty && (
              <div className="pt-8 sm:pt-16 text-center animate-fade-up">
                <div className="w-14 h-14 rounded-2xl bg-nerd-light flex items-center justify-center mx-auto mb-5">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">What are we making today?</h1>
                <p className="text-nerd-light/55 max-w-md mx-auto">Tell me what's in your fridge, snap a photo of your ingredients, or ask for any dish. I'll build a full recipe and refine it as we chat.</p>
              </div>
            )}

            {messages.map((m) => (
              <div key={m.id} data-testid={`msg-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} animate-fade-up`}>
                {m.role === "user" ? (
                  <div className="max-w-[85%] flex flex-col items-end gap-2">
                    {m.image_url && (
                      <img src={mediaUrl(m.image_url)} alt="upload" className="max-h-56 rounded-2xl rounded-tr-md object-cover border border-black/10" />
                    )}
                    {m.text && (
                      <div className="bg-nerd-light text-white px-4 py-2.5 rounded-2xl rounded-tr-md text-sm leading-relaxed whitespace-pre-wrap">{m.text}</div>
                    )}
                  </div>
                ) : (
                  <div className="max-w-[92%] w-full flex flex-col gap-3">
                    {m.text && (
                      <div className="bg-black/5 px-4 py-2.5 rounded-2xl rounded-tl-md text-sm leading-relaxed text-nerd-light/90 w-fit max-w-full whitespace-pre-wrap">{m.text}</div>
                    )}
                    {m.recipe && <ChatRecipeCard recipe={m.recipe} coverImage={m.cover_image} creatorName={m.creator_name} creatorHandle={m.creator_handle} />}
                  </div>
                )}
              </div>
            ))}

            {sending && <div className="flex justify-start animate-fade-up"><TypingBubble /></div>}
          </div>
        </div>

        {/* Input bar */}
        <div className="shrink-0 border-t border-black/8 bg-white px-4 sm:px-6 pb-4 pt-3">
          <div className="max-w-3xl mx-auto">
            {preview && (
              <div className="relative w-fit mb-2.5">
                <img src={preview} alt="preview" className="h-20 w-20 object-cover rounded-xl border border-black/10" />
                <button data-testid="remove-attachment-btn" onClick={clearAttachment}
                  className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-nerd-light text-white flex items-center justify-center hover:opacity-90">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            <div className="flex items-end gap-2 bg-white border border-black/12 rounded-3xl p-1.5 pl-2 focus-within:border-black/30 transition-colors shadow-sm">
              <button data-testid="attach-file-btn" onClick={() => fileInput.current?.click()}
                className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-nerd-light/70 hover:bg-black/5 transition-colors"
                title="Attach a photo">
                <Plus className="w-5 h-5" />
              </button>
              <input ref={fileInput} type="file" accept="image/*" className="hidden" data-testid="chat-file-input"
                onChange={(e) => onFile(e.target.files?.[0])} />
              <textarea
                ref={taRef}
                data-testid="chat-input"
                rows={1}
                value={input}
                onChange={(e) => { setInput(e.target.value); autoGrow(); }}
                onKeyDown={onKeyDown}
                placeholder="Describe your recipe"
                className="flex-1 resize-none bg-transparent outline-none text-sm py-2.5 leading-relaxed max-h-40 placeholder:text-nerd-light/40"
              />
              <button data-testid="voice-dictation-btn" onClick={toggleVoice}
                className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${listening ? "bg-red-500 text-white animate-pulse" : "text-nerd-light/70 hover:bg-black/5"}`}
                title={listening ? "Stop dictation" : "Dictate with your voice"}>
                <Mic className="w-5 h-5" />
              </button>
              <button data-testid="chat-send-btn" onClick={send} disabled={sending || (!input.trim() && !file)}
                className="shrink-0 w-10 h-10 rounded-full bg-nerd-light text-white flex items-center justify-center hover:opacity-90 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
